/**
 * Column order as a list of keys, first to last. Two operations, both pure.
 *
 * Until 2026-09 the order was the PlaylistColumns table's and was rewritten
 * from it on every load. Now the user owns it (View options can move
 * columns), so the table's order is only the starting point and the rule
 * for where a column added to the table later goes.
 */

/**
 * Where a column that is new to the table lands in an order the user may
 * have rearranged: right after the nearest column that precedes it in the
 * table and is present in the user's order, or first when none is. So a
 * "Disc" column added next to "Track #" shows up next to it wherever the
 * user has put "Track #", rather than at the end or at the table's index.
 */
export function orderWithNewColumn<T>(
  existing: readonly T[],
  tableOrder: readonly T[],
  newKey: T,
): T[] {
  if (existing.includes(newKey)) {
    return [...existing];
  }

  const tableIndex = tableOrder.indexOf(newKey);
  const predecessors = tableIndex === -1 ? [] : tableOrder.slice(0, tableIndex).reverse();
  const anchor = predecessors.find((key) => existing.includes(key));

  const result = [...existing];
  const at = anchor === undefined ? 0 : result.indexOf(anchor) + 1;

  result.splice(at, 0, newKey);

  return result;
}

/** The order with `key` moved to position `to`, clamped to the ends. */
export function moveColumn<T>(order: readonly T[], key: T, to: number): T[] {
  const from = order.indexOf(key);

  if (from === -1) {
    return [...order];
  }

  const result = [...order];
  result.splice(from, 1);
  result.splice(Math.max(0, Math.min(to, result.length)), 0, key);

  return result;
}
